import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { SubjectBadge } from '../components/SubjectBadge';
import { useActiveSubject } from '../context/ActiveSubjectContext';
import { getSubjectModulesCopy } from '../i18n/subjectModulesCopy';
import {
  OPTIONAL_SUBJECT_MODULE_CODES,
  SUBJECT_CONFIG,
  SUBJECT_MODULE_CODES,
  type OptionalSubjectModuleCode,
  type SubjectModuleCode,
} from '../utils/subjectConfig';

function isOptionalModule(code: SubjectModuleCode): code is OptionalSubjectModuleCode {
  return OPTIONAL_SUBJECT_MODULE_CODES.includes(code as OptionalSubjectModuleCode);
}

export function SubjectModulesPage() {
  const copy = useMemo(() => getSubjectModulesCopy(), []);
  const { loadedModules, setSubjectModuleLoaded } = useActiveSubject();
  const [expandedCode, setExpandedCode] = useState<SubjectModuleCode | null>(null);

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <h1 className="text-xl font-extrabold tracking-tight text-ink">{copy.title}</h1>
        <p className="mt-1 text-sm leading-6 text-muted">{copy.sectionHint}</p>
      </div>

      <div className="space-y-3">
        {SUBJECT_MODULE_CODES.map((code) => {
          const optional = isOptionalModule(code);
          const loaded = optional ? loadedModules[code] : true;
          const required = !optional;
          const expanded = expandedCode === code;
          const config = SUBJECT_CONFIG[code];
          const moduleCopy = copy.modules[code];

          return (
            <article
              key={code}
              className={`relative overflow-hidden rounded-2xl border border-border bg-surface shadow-[0_8px_24px_rgba(11,19,32,0.06)] transition ${
                loaded ? '' : 'bg-canvas'
              }`}
            >
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 w-1"
                style={{ backgroundColor: config.color }}
              />
              <span
                aria-hidden
                className="pointer-events-none absolute -right-6 -top-8 h-32 w-32 rounded-full"
                style={{ backgroundColor: `${config.color}14` }}
              />

              <div className="flex items-center gap-3 px-5 py-4">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  onClick={() => setExpandedCode(expanded ? null : code)}
                  aria-expanded={expanded}
                >
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${config.color}18` }}
                  >
                    <SubjectBadge subject={code} size="md" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-base font-extrabold text-ink">{moduleCopy.name}</span>
                      {optional ? (
                        <span className="rounded bg-[#FFF4CE] px-1.5 py-0.5 text-[10px] font-extrabold tracking-wide text-[#8A6116]">
                          {copy.beta}
                        </span>
                      ) : (
                        <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-extrabold tracking-wide text-primary-strong">
                          {copy.required}
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block text-xs font-semibold text-muted">{moduleCopy.tagline}</span>
                    <span className="mt-1 block text-[11px] text-muted">
                      {expanded ? copy.collapse : copy.expand}
                    </span>
                  </span>
                  {expanded
                    ? <ChevronUp size={18} className="shrink-0 text-muted" />
                    : <ChevronDown size={18} className="shrink-0 text-muted" />}
                </button>
                <button
                  type="button"
                  role="switch"
                  aria-checked={loaded}
                  disabled={required}
                  aria-label={copy.toggleA11y(moduleCopy.name)}
                  onClick={() => {
                    if (!optional) return;
                    setSubjectModuleLoaded(code, !loaded);
                  }}
                  className={`relative h-7 w-11 shrink-0 rounded-full transition ${
                    required ? 'cursor-not-allowed opacity-70' : 'hover:opacity-90'
                  }`}
                  style={{ backgroundColor: loaded ? config.color : '#C2CAD8' }}
                >
                  <span
                    className={`absolute top-0.5 h-[22px] w-[22px] rounded-full bg-white shadow-sm transition ${
                      loaded ? 'left-[22px]' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>

              {expanded ? (
                <div className="ml-[72px] mr-5 mb-5 space-y-3 border-t border-border pt-3">
                  <p className="text-sm leading-6 text-ink">{moduleCopy.body}</p>
                  <ul className="space-y-1.5">
                    {moduleCopy.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm font-semibold text-ink">
                        <span
                          className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: config.color }}
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs leading-5 text-muted">
                    {required ? copy.mathHint : copy.optionalHint}
                  </p>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    </div>
  );
}
